import { useEffect, useState } from 'react';
import { Outlet, useMatch, useParams, Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Drawer,
  Toolbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { type RootState, useAppDispatch } from '@/store';
import {
  fetchFacilities,
  setCurrentFacility,
  selectAllFacilities,
  selectFacilitiesLoading,
} from '@/store/slices/facilitiesSlice';
import { selectIsAuthenticated } from '@/store/slices/authSlice';
import { useSessionTimeout } from '@/hooks/useSessionTimeout';
import { useRealtimeLatencyBeacon } from '@/hooks/useRealtimeLatencyBeacon';
import { useOpenAlertsSeed } from '@/hooks/useOpenAlertsSeed';
import { wsService } from '@/services/websocket';
import { resetRealtimeData } from '@/store/slices/realtimeSlice';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import SkipToContent from '@/components/common/SkipToContent';
import GuidedWalkthrough from '@/components/onboarding/GuidedWalkthrough';
// SS-SEC-024 note: ImpersonationBanner now mounted globally via RootLayout in
// router.tsx, NOT per-layout. Keeps the persistent warning visible across
// /support-session, /facilities picker, /users, and every other top-level route.

const DRAWER_WIDTH = 280;

interface FacilityLayoutProps {
  darkMode: boolean;
  onToggleDarkMode: () => void;
}

type FacilityLookupStatus = 'idle' | 'pending' | 'fulfilled' | 'rejected';

export default function FacilityLayout({
  darkMode,
  onToggleDarkMode,
}: FacilityLayoutProps) {
  useSessionTimeout();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useAppDispatch();
  const { facilityId } = useParams<{ facilityId: string }>();
  const isFloorPlanEditor = Boolean(
    useMatch('/facilities/:facilityId/settings/floor-plan'),
  );
  const realtimeEnabled = !isFloorPlanEditor;

  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const facilities = useSelector(selectAllFacilities);
  const facilitiesLoading = useSelector(selectFacilitiesLoading);
  const currentFacility = useSelector(
    (state: RootState) => state.facilities.currentFacility,
  );
  const [facilityLookup, setFacilityLookup] = useState<{
    facilityId?: string;
    status: FacilityLookupStatus;
  }>({ status: 'idle' });
  const [facilityLookupAttempt, setFacilityLookupAttempt] = useState(0);
  const routeFacility = facilities.find((facility) => facility.id === facilityId);
  const facilityLookupMatchesRoute = facilityLookup.facilityId === facilityId;
  const facilityResolutionPending = !routeFacility
    && (
      facilitiesLoading
      || !facilityLookupMatchesRoute
      || facilityLookup.status === 'idle'
      || facilityLookup.status === 'pending'
    );
  const facilityResolutionFailed = !routeFacility
    && facilityLookupMatchesRoute
    && facilityLookup.status === 'rejected';
  const realtimeBlocked = !isAuthenticated || !routeFacility;

  // The floor-plan editor is a static configuration surface. Invalid facility
  // routes must not subscribe to realtime data under a stale prior context.
  useRealtimeLatencyBeacon(
    realtimeEnabled && routeFacility ? facilityId : undefined,
  );
  // F2-1: open alerts are seeded at the shell so every facility page (Header bell,
  // Sidebar badge, Dashboard KPI, Command Center) shares one authoritative list
  // instead of depending on which page happened to load first.
  useOpenAlertsSeed(isAuthenticated && routeFacility ? facilityId : undefined);
  // Header owns the normal realtime connection, but validation screens unmount
  // it. Close the singleton and clear facility-scoped data at that boundary.
  useEffect(() => {
    if (!realtimeBlocked) return;
    wsService.disconnect();
    dispatch(resetRealtimeData());
  }, [dispatch, realtimeBlocked]);
  // Security fix C6: JWT no longer in Redux. The httpOnly cookie is what
  // actually authorizes the request — gate on the auth flag instead.

  // Refresh the authorized list whenever a route ID is absent. The cached
  // Redux list can lag a newly created facility or a newly granted assignment.
  useEffect(() => {
    if (!isAuthenticated || !facilityId || routeFacility) return;

    const requestedFacilityId = facilityId;
    let active = true;
    setFacilityLookup({
      facilityId: requestedFacilityId,
      status: 'pending',
    });
    void dispatch(fetchFacilities()).unwrap().then(
      () => {
        if (!active) return;
        setFacilityLookup({
          facilityId: requestedFacilityId,
          status: 'fulfilled',
        });
      },
      () => {
        if (!active) return;
        setFacilityLookup({
          facilityId: requestedFacilityId,
          status: 'rejected',
        });
      },
    );

    return () => {
      active = false;
    };
  }, [
    isAuthenticated,
    facilityId,
    routeFacility,
    dispatch,
    facilityLookupAttempt,
  ]);

  // Sync the active facility into Redux so legacy components keep working
  useEffect(() => {
    if (!facilityId) {
      if (currentFacility) dispatch(setCurrentFacility(null));
      return;
    }
    if (routeFacility) {
      if (currentFacility?.id !== routeFacility.id) {
        dispatch(setCurrentFacility(routeFacility));
      }
      localStorage.setItem('lastSelectedFacilityId', facilityId);
      return;
    }
    if (currentFacility) dispatch(setCurrentFacility(null));
  }, [facilityId, routeFacility, currentFacility, dispatch]);

  // Redirect unauthenticated users to login — done AFTER all hooks
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (facilityResolutionPending) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CircularProgress size={22} />
          Loading facility...
        </Box>
      </Box>
    );
  }

  if (facilityResolutionFailed) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 3 }}>
        <Alert
          severity="error"
          action={(
            <Button
              color="inherit"
              size="small"
              onClick={() => setFacilityLookupAttempt((attempt) => attempt + 1)}
            >
              Retry
            </Button>
          )}
        >
          Unable to verify facility access. Check your connection and retry.
        </Alert>
      </Box>
    );
  }

  if (!routeFacility) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 3 }}>
        <Alert severity="error">
          Facility unavailable. It may not exist or your account may not have access.
        </Alert>
      </Box>
    );
  }

  const handleMenuClick = () => {
    if (isMobile) {
      setMobileOpen((o) => !o);
    } else {
      setDesktopOpen((o) => !o);
    }
  };

  const sidebarContent = (
    <Sidebar
      onClose={() => {
        if (isMobile) setMobileOpen(false);
        else setDesktopOpen(false);
      }}
      showCloseButton={!isMobile && desktopOpen}
    />
  );

  return (
    <Box sx={{ display: 'flex' }} role="application" aria-label="Sentry Solutions facility dashboard">
      <SkipToContent />
      <Header
        drawerOpen={desktopOpen}
        onMenuClick={handleMenuClick}
        darkMode={darkMode}
        onToggleDarkMode={onToggleDarkMode}
        facilityId={routeFacility.id}
        realtimeEnabled={realtimeEnabled}
      />

      <Box
        component="nav"
        id="facility-sidebar"
        aria-label="Facility navigation"
        sx={{
          width: { md: desktopOpen ? DRAWER_WIDTH : 0 },
          flexShrink: { md: 0 },
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
        >
          {sidebarContent}
        </Drawer>

        {/* Desktop persistent drawer */}
        <Drawer
          variant="persistent"
          open={desktopOpen}
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
            },
          }}
        >
          {sidebarContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        id="main-content"
        role="main"
        aria-label="Page content"
        tabIndex={-1}
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: {
            md: desktopOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%',
          },
          transition: theme.transitions.create(['width', 'margin-left'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          marginLeft: 0,
          overflow: 'hidden',
          minHeight: '100vh',
          bgcolor: 'background.default',
          // Remove default outline on programmatic focus (skip link target)
          '&:focus': { outline: 'none' },
        }}
      >
        <Toolbar />
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </Box>

      {/* Multi-page onboarding tour — mounted here so it survives route changes */}
      <GuidedWalkthrough />
    </Box>
  );
}
